import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, MessageCircle, Send } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import logger from '../utils/logger';
import { API_ENDPOINTS } from '../config/api';
import { useSanitizedForm } from '../hooks/useSanitizedInput';
import { sanitizeFormData } from '../utils/sanitizer';
import useRecaptcha from '../hooks/useRecaptcha';

const ContactPage = () => {
  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  };

  const { formData, updateField, reset } = useSanitizedForm(initialFormData, 'contact');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const { executeRecaptcha, isReady, error: recaptchaError } = useRecaptcha();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Determine field type for sanitization
    let fieldType = 'strict';
    if (name === 'email') fieldType = 'email';
    if (name === 'phone') fieldType = 'phone';
    if (name === 'message') fieldType = 'basic';
    
    updateField(name, value, fieldType);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Execute reCAPTCHA before form submission
      let recaptchaToken = null;
      if (isReady) {
        try {
          recaptchaToken = await executeRecaptcha('submit_contact');
        } catch (error) {
          logger.error('reCAPTCHA execution failed:', error);
          setSubmitStatus('error');
          setIsSubmitting(false);
          return;
        }
      } else if (recaptchaError) {
        logger.error('reCAPTCHA not ready:', recaptchaError);
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // Sanitize form data before sending
      const sanitizedData = sanitizeFormData(formData, 'contact');
      
      const response = await fetch(`${API_ENDPOINTS.CONTACT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...sanitizedData,
          recaptchaToken,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        reset(); // Reset form using the hook
      } else {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Error submitting contact form:', errorData);
        setSubmitStatus('error');
      }
    } catch (error) {
      logger.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50">
      {/* Header */}
      <header id="navigation" className="header-gradient shadow-lg" tabIndex="-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <BrandLogo className="h-10 md:h-14 w-auto" />
              <Link to="/" className="text-3xl font-bold text-white mr-4 hover:text-orange-100 transition-colors duration-300">מי פנוי</Link>
            </div>
            <div className="flex space-x-4 space-x-reverse">
              <Link
                to="/"
                className="text-white hover:text-orange-100 font-semibold text-lg transition-colors duration-300"
              >
                חזרה לדף הבית
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" tabIndex="-1">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-brown-900 mb-6">
            צור קשר
          </h1>
          <p className="text-xl text-brown-700 max-w-3xl mx-auto leading-relaxed">
            יש לך שאלה, בעיה או הצעה לשיפור? אנחנו כאן לעזור לך בכל דבר שתצטרך
          </p>
        </div>

        {/* Centered Contact Form */}
        <div className="flex justify-center">
          <div className="card max-w-2xl w-full hover-lift">
            <h2 className="text-3xl font-bold text-brown-900 mb-8 flex items-center justify-center">
              <div className="bg-orange-100 p-4 rounded-full ml-4">
                <MessageCircle className="h-8 w-8 text-orange-600" />
              </div>
              שלח לנו הודעה
            </h2>

            {submitStatus === 'success' && (
              <div className="mb-8 p-6 bg-success-50 border-2 border-success-200 rounded-2xl">
                <div className="flex items-center justify-center text-success-600 mb-3">
                  <Send className="h-6 w-6 ml-2" />
                  <span className="font-semibold text-lg">ההודעה נשלחה בהצלחה!</span>
                </div>
                <p className="text-success-700 text-center">
                  תודה שפנית אלינו. נחזור אליך בהקדם האפשרי.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-8 p-6 bg-error-50 border-2 border-error-200 rounded-2xl">
                <div className="flex items-center justify-center text-error-600 mb-3">
                  <span className="font-semibold text-lg">שגיאה בשליחת ההודעה</span>
                </div>
                <p className="text-error-700 text-center">
                  אירעה שגיאה בשליחת ההודעה. אנא נסה שוב מאוחר יותר או צור קשר בטלפון.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-name" className="form-label">שם מלא *</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    maxLength={100}
                    className="input-field"
                    placeholder="הזן את שמך המלא"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="form-label">אימייל *</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    maxLength={100}
                    className="input-field"
                    placeholder="הזן כתובת אימייל"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-phone" className="form-label">מספר טלפון</label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  maxLength={20}
                  className="input-field"
                  placeholder="הזן מספר טלפון (אופציונלי)"
                />
              </div>

              <div>
                <label htmlFor="contact-subject" className="form-label">נושא *</label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  maxLength={200}
                  className="input-field"
                  placeholder="מה הנושא של ההודעה שלך?"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="form-label">הודעה *</label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength={2000}
                  rows={6}
                  className="input-field resize-none"
                  placeholder="תאר את השאלה, הבעיה או ההצעה שלך..."
                />
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary text-lg px-12 py-4 disabled:opacity-50 disabled:cursor-not-allowed hover-lift"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                      שולח...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      שלח הודעה
                      <Send className="mr-2 h-5 w-5" />
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-20 text-center">
          <h3 className="text-3xl font-bold text-brown-900 mb-8">דרכי התקשרות נוספות</h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="card text-center hover-lift">
              <div className="bg-orange-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-orange-600" />
              </div>
              <h4 className="text-xl font-bold text-brown-900 mb-3">אימייל</h4>
              <p className="text-brown-700">info@mi-panui.co.il</p>
            </div>

            <div className="card text-center hover-lift">
              <div className="bg-orange-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-orange-600" />
              </div>
              <h4 className="text-xl font-bold text-brown-900 mb-3">שעות פעילות</h4>
              <p className="text-brown-700">א'-ה': 9:00-18:00</p>
              <p className="text-brown-700">ו': 9:00-14:00</p>
            </div>

            <div className="card text-center hover-lift">
              <div className="bg-orange-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-orange-600" />
              </div>
              <h4 className="text-xl font-bold text-brown-900 mb-3">תמיכה</h4>
              <p className="text-brown-700">אנחנו זמינים לעזור לך בכל שאלה או בעיה</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer id="footer" className="bg-gradient-to-r from-brown-800 to-brown-900 text-white mt-20" tabIndex="-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex justify-center items-center mb-6">
              <Truck className="h-8 w-8 text-orange-400 mr-3" />
              <h4 className="text-2xl font-bold text-orange-100">מי פנוי</h4>
            </div>
            <p className="text-brown-200 text-lg">
              © 2025 מי פנוי - הזמנת הובלות דירה. כל הזכויות שמורות.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
